import styled, { css, keyframes } from "styled-components";
import { fontFamilies } from "components/shared/tokens/typography";

const COLORS = {
  page: "#f5f7fc",
  white: "#ffffff",
  title: "#293142",
  muted: "#7d8599",
  lightMuted: "#a7afbf",
  line: "#e1e6ef",
  brand: "#ef4f5f",
  input: "#ffffff",
  error: "#c53c49",
};

const MOBILE = "@media (max-width: 480px)";
const PAGE_MAX_WIDTH = "760px";

export const ShippingNoteContent = styled.div`
  padding: 28px 28px 42px;

  ${MOBILE} {
    padding: 22px 18px 32px;
  }
`;

export const ShipmentSummaryCard = styled.section`
  overflow: hidden;
  border-radius: 22px;
  background: ${COLORS.white};
  box-shadow: 0 4px 14px rgba(41, 49, 66, 0.09);

  ${MOBILE} {
    border-radius: 18px;
  }
`;

export const ShipmentSummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  padding: 24px 16px 20px;

  ${MOBILE} {
    gap: 4px;
    padding: 18px 7px 16px;
  }
`;

export const SelectedInvoiceSummary = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  border-top: 1px solid ${COLORS.line};
  padding: 12px 20px;
  color: ${COLORS.title};
  font-family: ${fontFamilies.semiBold};
  font-size: 13px;

  svg {
    width: 16px;
    height: 16px;
    color: ${COLORS.muted};
  }

  ${MOBILE} {
    padding: 10px 14px;
    font-size: 12px;

    svg {
      width: 15px;
      height: 15px;
    }
  }
`;

export const ShipmentDetailsHeading = styled.h2`
  margin: 22px 0 14px;
  color: ${COLORS.title};
  font-family: ${fontFamilies.bold};
  font-size: 18px;
  line-height: 1.2;

  ${MOBILE} {
    margin: 18px 0 12px;
    font-size: 16px;
  }
`;

export const ShipmentFormCard = styled.section`
  border-radius: 22px;
  padding: 26px;
  background: ${COLORS.white};
  box-shadow: 0 3px 12px rgba(41, 49, 66, 0.06);

  ${MOBILE} {
    border-radius: 18px;
    padding: 20px 16px;
  }
`;

export const FormSectionLabel = styled.h3`
  margin: 0 0 14px;
  color: ${COLORS.title};
  font-family: ${fontFamilies.semiBold};
  font-size: 15px;

  ${MOBILE} {
    font-size: 13px;
  }
`;

export const DeliveryTypeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 28px;

  ${MOBILE} {
    gap: 10px;
    margin-bottom: 24px;
  }
`;

export const DeliveryTypeButton = styled.button`
  display: flex;
  min-height: 72px;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border: 1.5px solid
    ${({ $selected }) => ($selected ? COLORS.brand : COLORS.line)};
  border-radius: 14px;
  padding: 12px 14px;
  background: ${({ $selected }) => ($selected ? "#fff4f5" : COLORS.white)};
  color: ${({ $selected }) => ($selected ? COLORS.title : COLORS.muted)};
  font-family: ${fontFamilies.semiBold};
  font-size: 14px;
  line-height: 1.3;
  text-align: left;
  cursor: pointer;

  ${MOBILE} {
    min-height: 62px;
    padding: 10px 12px;
    font-size: 13px;
  }
`;

export const DeliveryRadio = styled.span`
  display: grid;
  width: 21px;
  height: 21px;
  flex: 0 0 21px;
  place-content: center;
  border: 1.5px solid ${COLORS.brand};
  border-radius: 50%;

  &::after {
    width: 11px;
    height: 11px;
    border-radius: 50%;
    background: ${COLORS.brand};
    content: "";
    opacity: ${({ $selected }) => ($selected ? 1 : 0)};
  }

  ${MOBILE} {
    width: 18px;
    height: 18px;
    flex-basis: 18px;

    &::after {
      width: 9px;
      height: 9px;
    }
  }
`;

export const DocumentSection = styled.section`
  margin: 0;
`;

export const DocumentTitle = styled.h4`
  margin: 0;
  color: ${COLORS.title};
  font-family: ${fontFamilies.semiBold};
  font-size: 14px;

  ${MOBILE} {
    font-size: 13px;
  }
`;

export const DocumentHelp = styled.p`
  margin: 5px 0 14px;
  color: ${COLORS.muted};
  font-size: 13px;
  line-height: 1.35;

  ${MOBILE} {
    font-size: 11px;
  }
`;

export const DocumentPickerRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
`;

export const DocumentPicker = styled.label`
  display: flex;
  width: 84px;
  height: 84px;
  align-items: center;
  justify-content: center;
  border: 1.5px dashed ${COLORS.brand};
  border-radius: 12px;
  background: #fffafb;
  color: ${COLORS.brand};
  cursor: pointer;

  svg {
    width: 28px;
    height: 28px;
  }

  ${MOBILE} {
    width: 70px;
    height: 70px;

    svg {
      width: 24px;
      height: 24px;
    }
  }
`;

export const HiddenFileInput = styled.input`
  display: none;
`;

export const DocumentPreview = styled.div`
  position: relative;
  width: 84px;
  height: 84px;

  img {
    display: block;
    width: 100%;
    height: 100%;
    border-radius: 11px;
    object-fit: cover;
  }

  ${MOBILE} {
    width: 70px;
    height: 70px;
  }
`;

export const RemoveDocumentButton = styled.button`
  position: absolute;
  top: -7px;
  right: -7px;
  display: grid;
  width: 23px;
  height: 23px;
  place-content: center;
  border: 0;
  border-radius: 50%;
  padding: 0;
  background: #aeb5c4;
  color: ${COLORS.white};
  cursor: pointer;

  svg {
    width: 14px;
    height: 14px;
  }
`;

export const FieldStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 22px;
`;

export const FieldWrapper = styled.div`
  position: relative;
`;

const fieldBorder = css`
  border: 1px solid ${({ $error }) => ($error ? COLORS.error : COLORS.line)};
`;

export const FieldLabel = styled.span`
  position: absolute;
  z-index: 1;
  top: -9px;
  left: 14px;
  padding: 0 6px;
  background: ${COLORS.white};
  color: ${({ $error }) => ($error ? COLORS.error : COLORS.lightMuted)};
  font-size: 13px;
`;

export const FieldInput = styled.input`
  ${fieldBorder};
  box-sizing: border-box;
  width: 100%;
  min-height: 52px;
  border-radius: 12px;
  padding: ${({ $hasSuffix }) => ($hasSuffix ? "0 52px 0 16px" : "0 16px")};
  background: ${COLORS.input};
  color: ${COLORS.title};
  font-family: ${fontFamilies.regular};
  font-size: 15px;
  outline: 0;

  &::placeholder {
    color: ${COLORS.lightMuted};
    opacity: 1;
  }

  &:focus {
    border-color: ${({ $error }) => ($error ? COLORS.error : COLORS.brand)};
  }

  ${MOBILE} {
    min-height: 46px;
    padding: ${({ $hasSuffix }) => ($hasSuffix ? "0 46px 0 14px" : "0 14px")};
    font-size: 13px;
  }
`;

export const InputInnerWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

export const InputSuffixButton = styled.button`
  position: absolute;
  right: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 0;
  border-radius: 8px;
  padding: 0;
  background: transparent;
  color: ${COLORS.brand};
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: #fff0f1;
  }

  &:active {
    background: #ffdde0;
  }

  svg {
    width: 20px;
    height: 20px;
    stroke-width: 1.8;
  }

  ${MOBILE} {
    right: 10px;
    width: 26px;
    height: 26px;

    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

export const FieldSelect = styled.select`
  box-sizing: border-box;
  width: 100%;
  min-height: 52px;
  appearance: none;
  border-radius: 12px;
  padding: 0 42px 0 16px;
  background: ${COLORS.white};
  color: ${({ value }) => (value ? COLORS.title : COLORS.lightMuted)};
  font-family: ${fontFamilies.regular};
  font-size: 15px;
  outline: 0;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1.5px solid ${({ $error }) => ($error ? COLORS.error : COLORS.line)};

  &:hover {
    border-color: ${COLORS.brand};
    box-shadow: 0 2px 8px rgba(239, 79, 95, 0.12);
  }

  &:focus {
    border-color: ${({ $error }) => ($error ? COLORS.error : COLORS.brand)};
    box-shadow: 0 0 0 3px rgba(239, 79, 95, 0.15);
  }

  option {
    background: ${COLORS.white};
    color: ${COLORS.title};
    padding: 12px 16px;
    line-height: 1.5;
    font-size: 13px;
  }

  option:hover {
    background: #fff4f5;
    color: ${COLORS.title};
  }

  option:checked {
    background: linear-gradient(${COLORS.brand}, ${COLORS.brand});
    background-color: #fff4f5 !important;
    color: ${COLORS.title};
  }

  ${MOBILE} {
    min-height: 46px;
    padding-left: 14px;
    font-size: 13px;

    option {
      font-size: 13px;
      padding: 10px 14px;
    }
  }
`;

export const SelectChevron = styled.span`
  position: absolute;
  top: 50%;
  right: 15px;
  display: grid;
  pointer-events: none;
  transform: translateY(-50%);
  color: ${COLORS.muted};

  svg {
    width: 19px;
    height: 19px;
  }
`;

export const PhoneDisclaimer = styled.p`
  margin: -13px 0 0;
  border-radius: 0 0 10px 10px;
  padding: 15px 16px 9px;
  background: #f3f5fa;
  color: ${COLORS.muted};
  font-size: 12px;
  line-height: 1.35;
`;

export const FieldError = styled.p`
  margin: 7px 0 0;
  color: ${COLORS.error};
  font-size: 12px;
  line-height: 1.35;
`;

export const SubmissionError = styled.div`
  margin: 0 28px 14px;
  border-radius: 10px;
  padding: 10px 12px;
  background: #fff0f1;
  color: ${COLORS.error};
  font-size: 13px;
  text-align: center;

  ${MOBILE} {
    margin: 0 18px 10px;
    font-size: 12px;
  }
`;

export const ModalBackdrop = styled.div`
  position: fixed;
  z-index: 1000;
  inset: 0;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 0;
  background: rgba(20, 24, 32, 0.72);
`;

export const ConfirmationModal = styled.section`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: ${PAGE_MAX_WIDTH};
  max-height: 86vh;
  max-height: 86dvh;
  overflow: hidden;
  border-radius: 22px 22px 0 0;
  background: ${COLORS.page};
  box-shadow: 0 18px 52px rgba(17, 23, 36, 0.32);

  ${MOBILE} {
    border-radius: 18px 18px 0 0;
  }
`;

export const ConfirmationBody = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 24px 28px 28px;
  -webkit-overflow-scrolling: touch;

  ${MOBILE} {
    padding: 20px 20px 24px;
  }
`;

export const ConfirmationIcon = styled.div`
  display: grid;
  width: 72px;
  height: 72px;
  place-content: center;
  border-radius: 50%;
  margin: 0 auto 18px;
  background: ${COLORS.white};
  color: ${COLORS.muted};

  img {
    width: 44px;
    height: 44px;
  }
`;

export const ConfirmationTitle = styled.h2`
  margin: 0 0 22px;
  color: ${COLORS.title};
  font-family: ${fontFamilies.bold};
  font-size: 24px;
  text-align: center;

  ${MOBILE} {
    font-size: 20px;
  }
`;

export const ConfirmationSummary = styled.dl`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 16px 20px;
  border-radius: 16px;
  margin: 0;
  padding: 24px 28px;
  background: ${COLORS.white};
  font-size: 13px;

  dt {
    color: ${COLORS.muted};
  }

  dd {
    overflow-wrap: anywhere;
    margin: 0;
    color: ${COLORS.title};
    font-family: ${fontFamilies.semiBold};
    text-align: right;
  }

  ${MOBILE} {
    gap: 14px 16px;
    padding: 20px 20px;
    font-size: 13px;
  }
`;

export const ConfirmationActions = styled.footer`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  border-top: 1px solid ${COLORS.line};
  padding: 18px 24px 22px;
  background: ${COLORS.white};

  ${MOBILE} {
    padding: 14px 17px 18px;
  }
`;

export const ModalButton = styled.button`
  min-height: 50px;
  border: 1.5px solid ${COLORS.brand};
  border-radius: 12px;
  padding: 0 12px;
  background: ${({ $primary }) => ($primary ? COLORS.brand : COLORS.white)};
  color: ${({ $primary }) => ($primary ? COLORS.white : COLORS.brand)};
  font-family: ${fontFamilies.semiBold};
  font-size: 15px;
  cursor: pointer;

  &:disabled {
    opacity: 0.65;
    cursor: wait;
  }

  ${MOBILE} {
    min-height: 46px;
    font-size: 13px;
  }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

export const Spinner = styled.span`
  display: inline-block;
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.5);
  border-top-color: ${COLORS.white};
  border-radius: 50%;
  animation: ${spin} 800ms linear infinite;
  vertical-align: middle;
`;

export const SuccessPage = styled.section`
  display: flex;
  min-height: 0;
  flex: 1;
  flex-direction: column;
  background: ${COLORS.page};
  overflow: hidden;
`;

export const SuccessBody = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 28px 24px 16px;
  text-align: center;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

export const SuccessIcon = styled.div`
  display: grid;
  width: 72px;
  height: 72px;
  place-content: center;
  border-radius: 50%;
  
  img {
    width: 72px;
    height: 72px;
  }
`;

export const SuccessTitle = styled.h1`
  margin: 12px 0 6px;
  color: ${COLORS.title};
  font-family: ${fontFamilies.bold};
  font-size: 22px;
`;

export const SuccessActions = styled.footer`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  border-top: 1px solid ${COLORS.line};
  padding: 14px 28px 20px;
  background: ${COLORS.white};
  box-shadow: 0 -7px 24px rgba(41, 49, 66, 0.06);
  flex: 0 0 auto;

  ${MOBILE} {
    padding: 12px 18px 16px;
    gap: 10px;
  }
`;

export const SuccessActionButton = styled.button`
  display: inline-flex;
  min-height: 48px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1.5px solid ${COLORS.brand};
  border-radius: 12px;
  background: ${({ $primary, $textLink }) => ($primary ? COLORS.brand : ($textLink ? 'transparent' : COLORS.white))};
  color: ${({ $primary, $textLink }) => ($primary ? COLORS.white : COLORS.brand)};
  font-family: ${fontFamilies.semiBold};
  font-size: 16px;
  cursor: pointer;
  ${({ $textLink }) => $textLink && `
    border: none;
  `}

  svg {
    width: 20px;
    height: 20px;
    stroke-width: 1.8;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }

  ${MOBILE} {
    min-height: 44px;
    font-size: 14px;

    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

export const HelpImageContainer = styled.div`
  align-self: stretch;
  flex: none;
  width: 100%;
  padding: 20px 0 4px;

  img {
    display: block;
    width: 100%;
    height: auto;
    max-width: 100%;
    border-radius: 8px;
  }

  ${MOBILE} {
    padding: 16px 0 4px;
  }
`;

export const CustomDropdownWrapper = styled.div`
  position: relative;
`;

export const CustomDropdownButton = styled.button`
  position: relative;
  width: 100%;
  min-height: 52px;
  text-align: left;
  border: 1px solid ${({ $error }) =>
    $error ? COLORS.error : COLORS.line};
  border-radius: 12px;
  padding: 0 16px;
  background: ${COLORS.white};
  color: ${({ $selected }) => ($selected ? COLORS.title : COLORS.lightMuted)};
  font-family: ${fontFamilies.regular};
  font-size: 15px;
  line-height: 1.4;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;

  span {
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
  }

  &:hover {
    box-shadow: 0 2px 8px rgba(41, 49, 66, 0.08);
  }

  &:focus-visible {
    outline: none;
    border-color: ${COLORS.line};
    box-shadow: 0 0 0 3px rgba(41, 49, 66, 0.05);
  }

  ${MOBILE} {
    min-height: 46px;
    padding: 0 14px;
    font-size: 13px;
  }
`;

export const CustomDropdownLabel = styled.span`
  position: absolute;
  top: -9px;
  left: 14px;
  z-index: 1;
  padding: 0 6px;
  background: ${COLORS.white};
  color: ${({ $error }) => ($error ? COLORS.error : COLORS.lightMuted)};
  font-size: 13px;
  font-family: ${fontFamilies.regular};
`;

export const CustomDropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  margin-top: 4px;
  border: 1.5px solid ${COLORS.line};
  border-radius: 12px;
  background: ${COLORS.white};
  box-shadow: 0 4px 16px rgba(41, 49, 66, 0.15);
  overflow: hidden;
  max-height: 300px;
  overflow-y: auto;

  ${MOBILE} {
    max-height: 250px;
  }
`;

export const CustomDropdownOption = styled.button`
  display: block;
  width: 100%;
  padding: 14px 16px;
  border: none;
  background: ${({ $selected }) => ($selected ? "#fff4f5" : COLORS.white)};
  color: ${COLORS.title};
  text-align: left;
  font-family: ${fontFamilies.regular};
  font-size: 15px;
  cursor: pointer;
  transition: background 0.15s ease;
  border-left: 3px solid ${({ $selected }) => ($selected ? COLORS.brand : "transparent")};

  &:hover {
    background: #fff4f5;
  }

  ${MOBILE} {
    font-size: 14px;
    padding: 12px 14px;
  }
`;


export const ShareButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0 0 20px;
  border: none;
  padding: 0;
  background: transparent;
  color: ${COLORS.brand};
  font-size: 14px;
  font-family: inherit;
  font-weight: 600;
  cursor: pointer;
`;

export const DocumentPreviewLink = styled.a`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  border-radius: 11px;
  background: #f0f2f7;
  color: ${COLORS.muted};
  gap: 4px;
  text-decoration: none;
`;
